import { MbeInput, PasswordFeedback, PasswordInput, TermsSignupMessage } from '@/components/shared';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Flex,
  Heading,
  Link,
  useControllableState,
  useDisclosure,
} from '@chakra-ui/react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import type { IBaseForm, ICompany } from '@/types';
import {
  emailRegex,
  ERRORS,
  hasDigits,
  hasSpecialChar,
  hasUpperLetter,
  minLength,
  phoneRegex,
  ROUTE_PATHS,
  shouldRequireVat,
} from '@/constants';
import { hasEmptyFields, hasFormAnyError } from '@/utils';
import { AddressFields } from '@/components/shared/AddressFields';
import { useUserApi, useValidator } from '@/features/users';

const initialRequiredFields = ['firstName', 'lastName', 'company', 'phone', 'countryCode'];

export const CompanySignUpForm: React.FC<IBaseForm<ICompany>> = ({
  defaultValues,
  onSubmit: providedOnSubmit,
}): JSX.Element => {
  const { i18n, t } = useTranslation();
  const { validateVatNumber } = useValidator();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const userApi = useUserApi();
  const [isMailUsed, setIsMailUsed] = useControllableState({ defaultValue: false });

  const methods = useForm<ICompany>({
    defaultValues,
    mode: 'onBlur',
    criteriaMode: 'all',
  });

  const {
    formState: { errors },
    getValues,
    handleSubmit: submitHandler,
    register,
    watch,
  } = methods;

  const isVatRequired = shouldRequireVat(watch('countryCode'));

  const requiredFields = isVatRequired
    ? [...initialRequiredFields, 'vatNumber']
    : initialRequiredFields;

  const areRequiredFieldsEmpty = hasEmptyFields<ICompany, Extract<keyof ICompany, string>>(
    watch(),
    requiredFields as unknown as Extract<keyof ICompany, string>[]
  );

  const isDisabled = areRequiredFieldsEmpty || isMailUsed || hasFormAnyError(errors);

  const handleSubmit: SubmitHandler<ICompany> = (data, event): void => {
    event?.preventDefault();

    const updateCompanyInfo = { ...data, customerType: 'BUSINESS', locale: i18n.language };

    providedOnSubmit?.(updateCompanyInfo);
  };

  const countryCodeValue = getValues('countryCode') && getValues('countryCode').toLowerCase();

  const vatNumberProps = register('vatNumber', {
    validate: (value) => validateVatNumber({ countryCode: countryCodeValue, vatNumber: value }),
  });

  return (
    <FormProvider {...methods}>
      <form
        autoComplete="off"
        id="signUpForm"
        name="signUpForm"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={submitHandler(handleSubmit)}
      >
        <Flex direction="column" gap={12}>
          <MbeInput
            error={errors.firstName}
            id="firstName"
            placeholder={t('FORM.FIRSTNAME')}
            {...register('firstName', {
              required: { value: true, message: t(ERRORS.empty) },
            })}
          />
          <MbeInput
            error={errors.lastName}
            id="lastName"
            placeholder={t('FORM.LASTNAME')}
            {...register('lastName', {
              required: { value: true, message: t(ERRORS.empty) },
            })}
          />
          <MbeInput
            error={errors.company}
            id="company"
            placeholder={t('FORM.COMPANY')}
            {...register('company', {
              required: { value: true, message: t(ERRORS.empty) },
            })}
          />
          <MbeInput
            error={errors.vatNumber}
            id="vatNumber"
            placeholder={t('FORM.VAT')}
            {...vatNumberProps}
          />
          <MbeInput
            error={errors.email}
            id="email"
            placeholder={t('FORM.EMAIL')}
            {...register('email', {
              required: { value: true, message: t(ERRORS.empty) },
              pattern: { value: emailRegex, message: t(ERRORS.email) },
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onBlur: async (): Promise<void> => {
                const isMailKnown: boolean = await userApi.checkUserEmail(getValues('email'));
                setIsMailUsed(isMailKnown);
              },
            })}
          />
          {isMailUsed && (
            <Alert my={-8} status="error" w="100%">
              <AlertIcon />
              <AlertDescription>
                <Trans
                  components={{
                    url: <Link color="brand.red.light" href={ROUTE_PATHS.userLogin} />,
                  }}
                  i18nKey="ERRORS.ALREADY_USED_EMAIL"
                />
              </AlertDescription>
            </Alert>
          )}
          <MbeInput
            error={errors.phone}
            id="phone"
            placeholder={t('FORM.PHONE')}
            {...register('phone', {
              required: { value: true, message: t(ERRORS.empty) },
              pattern: { value: phoneRegex, message: t(ERRORS.phoneNumber) },
            })}
          />
          <Heading as="h2" mb={-8} size="md">
            {t('SIGNUP.FORM.ADDRESS_FIELD_TITLE')}
          </Heading>
          <AddressFields />
          <Heading as="h2" mb={-8} size="md">
            {t('SIGNUP.FORM.PASSWORD_FIELD_TITLE')}
          </Heading>
          <PasswordFeedback
            error={errors.password}
            isOpen={isOpen}
            value={getValues('password')}
            onClose={onClose}
          >
            <PasswordInput
              error={errors.password}
              id="password"
              placeholder={t('FORM.PASSWORD')}
              {...register('password', {
                required: true,
                validate: {
                  minLength: (value) => minLength(value, 8),
                  hasUpperLetter,
                  hasSpecialChar,
                  hasDigits,
                },
                onBlur: onClose,
                onChange: () => {
                  if (!isOpen) onOpen();
                },
              })}
            />
          </PasswordFeedback>
          <TermsSignupMessage />
        </Flex>
        <Button disabled={isDisabled} mt={8} type="submit" variant="mbeSecondary" w="full">
          {t('ACTIONS.CONFIRM')}
        </Button>
      </form>
    </FormProvider>
  );
};
